/**
 * Actor derivation for RMA cases.
 *
 * Every non-terminal status maps to exactly one actor — the party that owns
 * the next move. This is the primary signal in the UI per the audit doc
 * §10 "actor-centric design" principle:
 *   - "Your turn"          — Staff must act
 *   - "Waiting on customer" — Customer must ship / respond / pay
 *   - "Waiting on vendor"   — Vendor must respond / approve / credit
 *   - "System handling"     — Automation is in-flight (LSM / carrier)
 *   - "Done"                — Terminal, read-only
 *
 * Used by: SituationHeader. (`CaseStatusBar` stand hier bis 2026-09-08 als
 * zweiter Verbraucher — die Datei ist geloescht.) Auf der
 * FALL-DETAILSEITE. Dort gibt es kein `situation` im Payload, und diese Datei
 * bleibt die Quelle.
 *
 * 🔴 **Die ÜBERSICHT fragt dieses Modul seit dem 2026-08-14 nicht mehr**
 * (P2b Bahn B1). `RMACasesDashboardBody.jsx` liest `c.situation` aus der
 * Nutzlast von `GET /api/rma/overview` und übersetzt es in `rma/situation.js`.
 * Die Zeile in dieser Kopfzeile, die früher „RMAOverview (column + filter)" und
 * „RMAInbox (per-row tag)" nannte, war ohnehin schon falsch — weder die
 * Übersicht noch der Posteingang importierten je etwas von hier.
 *
 * ⚠️ **Warum die Übersicht wechseln musste, und warum das kein Löschgrund für
 * diese Datei ist.** `deriveActor` endet unten mit `return ACTOR_STAFF` als
 * Fall-Through: jeder Status, den es nicht kennt, liest sich als „Your turn".
 * Der SQL-CASE hinter `situation` trennt genau diesen Fall als `unmapped` ab.
 * An `RMA-2099-0007` (`sent`/`inquiry`) gemessen: hier `staff`, dort
 * `unmapped`. Solange die Übersicht selbst ableitete, sagten Zeile und
 * Facettenpille über denselben Fall zwei verschiedene Dinge.
 *
 * 🔴 **Wer `deriveActor`/`ActorBadge` in `RMACasesDashboardBody.jsx` (oder eine
 * andere Overview-Fläche) zurückverdrahtet, stellt diese Divergenz wieder her.**
 * Gepinnt von `RMACasesDashboardBody.turn.test.jsx` (Test 4: eine Zeile, deren
 * `situation` dem widerspricht, was `deriveActor` aus `status`/`case_type`
 * ableiten würde).
 */

export const ACTOR_STAFF    = 'staff'
export const ACTOR_CUSTOMER = 'customer'
export const ACTOR_VENDOR   = 'vendor'
export const ACTOR_SYSTEM   = 'system'
export const ACTOR_READONLY = 'readonly'

/**
 * Der Agent des Kundendienstes als Urheber eines Ereignisses (Schnitt S0-A,
 * Spezifikation §11.3 und §15).
 *
 * 🔴 Er kommt NICHT aus `deriveActor`: der Status sagt nicht, WER gehandelt
 * hat. Der Wert wird beim SCHREIBEN gesetzt.
 *
 * Zwilling von `backend/rma/actors_py.py::ACTOR_AGENT`. Die Paritaet halten
 * `backend/tests/agent/test_actor_agent.py` P-A3 (liest diese Datei als Text)
 * und `actors.test.js` (die JS-Haelfte).
 */
export const ACTOR_AGENT = 'agent:kundendienst'

export const TERMINAL_STATUSES = ['resolved', 'rejected', 'cancelled']

/**
 * deriveActor(caseData) → one of ACTOR_* constants.
 *
 * Logic mirrors the state machine + clickdummy (rma-flow-clickdummy-2026-04-21.html).
 * If a status isn't in the map we fall back to ACTOR_STAFF — unknown state
 * always surfaces for staff review, never hides silently.
 */
export function deriveActor(caseData) {
  if (!caseData) return ACTOR_STAFF
  const { status, case_type } = caseData

  if (TERMINAL_STATUSES.includes(status)) return ACTOR_READONLY

  if (case_type === 'return') {
    // Return-only statuses
    if (status === 'awaiting_return')                 return ACTOR_CUSTOMER
    if (status === 'in_transit' || status === 'received') return ACTOR_SYSTEM
    // Staff-action statuses on the return flow
    if ([
      'open', 'draft', 'return_requested', 'return_created',
      'booked', 'graded', // graded kept for pre-migration-069 cases
      'approved',
    ].includes(status)) return ACTOR_STAFF
  }

  // Service flow (also: inquiry cases fall through here sensibly)
  if (status === 'with_vendor')       return ACTOR_VENDOR
  if (status === 'with_customer')     return ACTOR_CUSTOMER
  if (status === 'awaiting_shipment') return ACTOR_SYSTEM
  if ([
    'open', 'draft', 'confirmed', 'action_needed',
    'agreed', 'resolution_agreed', // legacy name kept for safety
    'approved',
  ].includes(status)) return ACTOR_STAFF

  return ACTOR_STAFF
}

/**
 * Config for rendering each actor. Kept alongside the derivation so callers
 * have one import. `label` is the "whose turn" sentence used in the hero
 * strip + overview column. `shortLabel` is the compact chip version.
 */
export const ACTOR_CONFIG = {
  [ACTOR_STAFF]: {
    label:      'Your turn',
    shortLabel: 'You',
    description: 'Staff must act next.',
    color:      'warning',
    chipColor:  'warning',
    accent:     'warning.main',
    bg:         'rgba(251,191,36,0.10)',
  },
  [ACTOR_CUSTOMER]: {
    label:      'Waiting on customer',
    shortLabel: 'Customer',
    description: 'Waiting for customer — shipment, response, or info.',
    color:      'info',
    chipColor:  'info',
    accent:     'info.main',
    bg:         'rgba(96,165,250,0.10)',
  },
  [ACTOR_VENDOR]: {
    label:      'Waiting on vendor',
    shortLabel: 'Vendor',
    description: 'Waiting for vendor response / approval / credit.',
    color:      'secondary',
    chipColor:  'secondary',
    accent:     'secondary.main',
    bg:         'rgba(167,139,250,0.10)',
  },
  [ACTOR_SYSTEM]: {
    label:      'System handling',
    shortLabel: 'System',
    description: 'Automation is in flight — LSM / carrier tracking.',
    color:      'default',
    chipColor:  'default',
    accent:     'text.secondary',
    bg:         'rgba(148,163,184,0.10)',
  },
  [ACTOR_READONLY]: {
    label:      'Done',
    shortLabel: 'Done',
    description: 'Case closed — read-only.',
    color:      'success',
    chipColor:  'success',
    accent:     'success.main',
    bg:         'rgba(74,222,128,0.10)',
  },
}

export function actorConfigFor(caseData) {
  return ACTOR_CONFIG[deriveActor(caseData)] || ACTOR_CONFIG[ACTOR_STAFF]
}

/** Useful for "Your open tasks" / "Needs attention" filters. */
export function isStaffTurn(caseData) {
  return deriveActor(caseData) === ACTOR_STAFF
}
